"use client";

import { useEffect, useRef } from "react";

type AutoPlayVideoProps = {
  src: string;
  poster?: string;
  className?: string;
};

type ConnectionNavigator = Navigator & { connection?: { saveData?: boolean } };

export default function AutoPlayVideo({ src, poster, className = "" }: AutoPlayVideoProps) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    // Respect reduced motion and data saver: show the poster only, download nothing.
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = (navigator as ConnectionNavigator).connection?.saveData;
    if (reduceMotion || saveData) return;

    // React doesn't always write the `muted` attribute to the DOM, and autoplay needs it.
    video.muted = true;

    let loaded = false;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Start downloading only when the section is about to be seen.
          if (!loaded) {
            video.src = src;
            video.load();
            loaded = true;
          }
          video.play().catch(() => {});
        } else {
          video.pause(); // no decoding or battery use while off screen
        }
      },
      { rootMargin: "200px", threshold: 0.1 },
    );

    io.observe(video);
    return () => io.disconnect();
  }, [src]);

  return (
    <video
      ref={ref}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden="true"
      tabIndex={-1}
      className={className}
    />
  );
}